import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-08e421b0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "spelling-word-detail" }
const _hoisted_2 = { class: "detail-wrapper" }
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "mobile-back" }
const _hoisted_5 = ["src"]
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "back-text" }, "Back", -1))
const _hoisted_7 = { class: "page-title-wrapper" }
const _hoisted_8 = { class: "work-sans" }
const _hoisted_9 = { class: "student-name-heading" }
const _hoisted_10 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Loading word data...", -1))
const _hoisted_12 = {
  key: 1,
  class: "detail-content"
}
const _hoisted_13 = {
  key: 0,
  class: "summary-loader"
}
const _hoisted_14 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "summary-loader__text" }, "Loading word data...", -1))
const _hoisted_15 = { class: "summary-head" }
const _hoisted_16 = { class: "summary-title" }
const _hoisted_17 = { class: "eyebrow" }
const _hoisted_18 = { class: "level-info" }
const _hoisted_19 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "level-separator" }, "|", -1))
const _hoisted_20 = { class: "level-status" }
const _hoisted_21 = { class: "etymology-section" }
const _hoisted_22 = ["innerHTML"]
const _hoisted_23 = { class: "exposure-table" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "flex items-center gap-2" }
const _hoisted_26 = { class: "status-text" }
const _hoisted_27 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_28 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Couldn't load this word", -1))
const _hoisted_29 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Something went wrong while loading this word's data. Please check your connection and try again.", -1))
const _hoisted_30 = {
  key: 3,
  class: "empty-state"
}
const _hoisted_31 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Word not found", -1))
const _hoisted_32 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "We could not locate that spelling word. Please return to the list and choose another word.", -1))
const _hoisted_33 = [
  _hoisted_31,
  _hoisted_32
]
const _hoisted_34 = ["innerHTML"]

import { computed, ref, onMounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLingoStore } from "@/stores/lingoStore";
import { useLearnerStore } from "@/stores/learnerStore";
import i18n from "@/plugins/i18n";
import type { KnowledgeComponent, ExposureHistory } from "@/interfaces/lingo.interfaces";
import LingoDataTable from "@/modules/main/components/manage/LingoDataTableComponent.vue";
import LingoModal from "@/modules/main/components/manage/LingoModalComponent.vue";
import LingoTooltipIcon from "@/modules/main/components/manage/LingoTooltipIconComponent.vue";
import AnswerReviewModal from "@/components/manage/AnswerReviewModal.vue";
import BlockedPracticeFullExplanationModal from "@/components/manage/BlockedPracticeFullExplanationModal.vue";
import getKcStatusTooltip from "@/modules/main/utils/kc";
import { logger } from "@/utils/logger";
import { WORD_NOT_FOUND_EVENT, buildWordNotFoundLog } from "@/utils/lingo-word-not-found";
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?component';
import BuildingIcon from '@/assets/icons/building-05.svg?component';
import SpinnerLoader from "@/components/global/SpinnerLoader.vue";
import api from "@/api";


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoStudentSpellingKC',
  setup(__props) {

const isArrowIconString = typeof ArrowLeftIcon === "string";

const isLoading = ref(false);

// Route and store
const route = useRoute();
const router = useRouter();
const lingoStore = useLingoStore();
const learnerStore = useLearnerStore();

// Grab route params (path: /main/lingo-spelling-word/:studentId/:kcId)
const studentId = computed(() => String(route.params.studentId ?? ""));
const kcIdParam = computed(() => String(route.params.kcId ?? ""));
const normalizedKcId = computed(() => {
  const raw = kcIdParam.value;
  if (!raw) return null;
  const decoded = decodeURIComponent(String(raw));
  const asNumber = Number(decoded);
  if (Number.isFinite(asNumber) && String(asNumber) === decoded) return asNumber;
  return decoded;
});

// Get the student Lingo data from the store
const studentLingoData = computed(() => lingoStore.getLingoDataByStudentId(studentId.value) || null);

//breadcrumbs
const pages = computed(() => {
  const base = [{ name: "Lingo Data", path: "/main/lingo-data" }];
  base.push({
    name: "Data for " + displayName.value,
    path: "/main/lingo-data/student/" + studentId.value
  });
  base.push({
    name: `Spell the word: ${kc.value?.word || ""}`,
    path: "#"
  });
  return base;
});


const searchableKcs = computed(() => {
  const allKcs = Array.isArray(studentLingoData.value?.allKCs) ? studentLingoData.value?.allKCs : [];
  const currentKcs = Array.isArray(studentLingoData.value?.currentSpellingActivityKcs)
    ? studentLingoData.value?.currentSpellingActivityKcs
    : [];

  const merged = new Map<string, any>();
  const buildKey = (kc: any) => {
    if (!kc || typeof kc !== "object") return null;
    const type = String(kc.type || "spelling");
    const idPart =
      kc.strapiWordId != null
        ? `strapi:${String(kc.strapiWordId)}`
        : kc._id != null
          ? `mongo:${String(kc._id)}`
          : kc.id != null
            ? `id:${String(kc.id)}`
            : kc.word != null
              ? `word:${String(kc.word).toLowerCase()}`
              : null;
    return idPart ? `${type}:${idPart}` : null;
  };
  const getHistorySize = (kc: any) =>
    Array.isArray(kc?.exposureHistory) ? kc.exposureHistory.length : 0;

  const upsert = (kc: any) => {
    const key = buildKey(kc);
    if (!key) return;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, kc);
      return;
    }
    if (getHistorySize(kc) > getHistorySize(existing)) {
      merged.set(key, kc);
    }
  };

  allKcs.forEach(upsert);
  currentKcs.forEach(upsert);

  return [...merged.values()].filter((kc: any) => kc && kc.type === "spelling");
});

// Resolve the KC for this student by matching word, strapiWordId, _id or id
const kc = computed<KnowledgeComponent | null>(() => {
  if (normalizedKcId.value == null) return null;
  const searchId = String(normalizedKcId.value);
  const all = searchableKcs.value || [];

  let found = all.find((k: any) => k?.strapiWordId != null && String(k.strapiWordId) === searchId);

  if (!found) {
    found = all.find((k: any) => k?.word != null && String(k.word) === searchId);
  }

  if (!found) {
    found = all.find((k: any) => String(k.id ?? "") === searchId || String(k._id ?? "") === searchId);
  }

  if (!found) {
    const lower = searchId.toLowerCase();
    found = all.find((k: any) => typeof k?.word === "string" && k.word.toLowerCase() === lower);
  }

  return found || null;
});

// Simple mapping for status label used in template
const STATUS_LABELS: Record<string, string> = {
  not_yet_seen: "Not yet seen",
  seen: "Seen",
  learning: "Learning",
  practicing: "Reinforcing",
  mastered_prior_learning: "Mastered (Prior Learning)",
  mastered_new_learning: "Mastered (Gap Filled)",
};

const statusBadge = computed(() => {
  if (!kc.value) return { label: "Not available", color: "gray", description: "" };
  const label = STATUS_LABELS[kc.value.status] || STATUS_LABELS.not_yet_seen;
  return { label, color: "gray", description: "" };
});

// Tooltip text / visible description for current KC status
const statusDescriptionKey = computed(() => {
  return getKcStatusTooltip(kc.value?.status);
});

const statusDescription = computed(() => {
  const key = statusDescriptionKey.value;
  if (!key) return "";
  return i18n.global.t(key).toString();
});

// Format exposure history rows for the table
function formatTimestamp(ts: string | Date | undefined | null) {
  if (!ts) return "—";
  const d = new Date(ts as any);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }).format(d);
  } catch {
    return "—";
  }
}

function mapStatusLabel(raw: string | null | undefined) {
  if (!raw) return null;
  const key = String(raw).trim();
  if (!key) return null;
  if (STATUS_LABELS[key as keyof typeof STATUS_LABELS]) {
    return STATUS_LABELS[key as keyof typeof STATUS_LABELS];
  }
  if (/^true$/i.test(key) || /^correct$/i.test(key)) return "Correct";
  if (/^false$/i.test(key) || /^incorrect$/i.test(key)) return "Incorrect";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function parseOptionalDate(value: any): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function normalizeToken(value: any): string {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

function isBlockedTaggedEntry(entry: any): boolean {
  if (!entry || typeof entry !== "object") {
    return false;
  }
  return Boolean(
    normalizeToken(entry.activityMode) === "blocked" ||
    (typeof entry.blockedSessionKey === "string" && entry.blockedSessionKey.trim().length) ||
    (typeof entry.blockedAggregateLabel === "string" && entry.blockedAggregateLabel.trim().length) ||
    parseOptionalDate(entry.blockedCompletedAt)
  );
}

function sortBlockedSessionEntries(entries: ExposureHistory[]): ExposureHistory[] {
  return [...(Array.isArray(entries) ? entries : [])].sort((left: any, right: any) => {
    const leftIndex = Number(left?.blockedQuestionIndex);
    const rightIndex = Number(right?.blockedQuestionIndex);
    const leftHasIndex = Number.isFinite(leftIndex);
    const rightHasIndex = Number.isFinite(rightIndex);

    if (leftHasIndex && rightHasIndex && leftIndex !== rightIndex) {
      return leftIndex - rightIndex;
    }
    if (leftHasIndex && !rightHasIndex) return -1;
    if (!leftHasIndex && rightHasIndex) return 1;

    const leftTs = parseOptionalDate(left?.timestamp);
    const rightTs = parseOptionalDate(right?.timestamp);
    if (!leftTs && !rightTs) return 0;
    if (!leftTs) return -1;
    if (!rightTs) return 1;
    return leftTs.getTime() - rightTs.getTime();
  });
}

function isBlockedAggregateCompleteEntry(entry: any): boolean {
  if (!entry || typeof entry !== "object") return false;
  if (parseOptionalDate(entry.blockedCompletedAt)) {
    return true;
  }

  const blockedMode = normalizeToken(entry.activityMode) === "blocked";
  if (!blockedMode) {
    return false;
  }

  const hasAggregateLabel =
    typeof entry.blockedAggregateLabel === "string" &&
    entry.blockedAggregateLabel.trim().length > 0;
  const hasFinalStatus =
    typeof entry.blockedFinalStatus === "string" &&
    entry.blockedFinalStatus.trim().length > 0;

  return hasAggregateLabel || hasFinalStatus;
}

function getBlockedAggregateSessionKey(entry: any, fallbackRow: any = {}): string | null {
  if (!entry || typeof entry !== "object") return null;
  if (typeof entry.blockedSessionKey === "string" && entry.blockedSessionKey.trim().length) {
    return entry.blockedSessionKey.trim();
  }
  const completedAt = parseOptionalDate(entry.blockedCompletedAt);
  const primaryWord = entry.word || fallbackRow.word || kc.value?.word || "unknown-word";
  if (completedAt) {
    return `blocked:${primaryWord}:${completedAt.toISOString()}`;
  }

  const blockedMode = normalizeToken(entry.activityMode) === "blocked";
  if (!blockedMode) {
    return null;
  }

  const blockedLabel =
    typeof entry.blockedAggregateLabel === "string"
      ? entry.blockedAggregateLabel.trim().toLowerCase()
      : "";
  if (!blockedLabel) {
    return null;
  }

  const timestamp = parseOptionalDate(entry.timestamp);
  const daySegment = timestamp ? timestamp.toISOString().slice(0, 10) : "unknown-day";
  return `blocked:${primaryWord}:${blockedLabel}:${daySegment}`;
}

function getExposureQuestionLabel(entry: any, fallbackRow: any = {}): string {
  const blockedLabel = typeof entry?.blockedAggregateLabel === "string" ? entry.blockedAggregateLabel.trim() : "";
  if (blockedLabel) {
    return blockedLabel;
  }
  if (entry && typeof entry.isCorrect === "boolean") {
    return entry.isCorrect ? "Correct" : "Incorrect";
  }
  if (typeof fallbackRow?.isCorrect === "boolean") {
    return fallbackRow.isCorrect ? "Correct" : "Incorrect";
  }
  return "—";
}

function getExposureStatusLabel(entry: any): string {
  const defaultLabel = "Seen";
  if (!entry || typeof entry !== "object") return defaultLabel;

  const statusCandidates = [
    entry.blockedFinalStatus,
    entry.kcStatusAfter,
    entry.kcStatus,
    entry.statusAfter,
    entry.status,
    entry.resultStatus,
    entry.statusLabel,
    entry.progressStatus,
    entry.result && entry.result.status
  ];

  for (const candidate of statusCandidates) {
    const label = mapStatusLabel(candidate);
    if (label) return label;
  }

  return defaultLabel;
}

function getExposureStatusSource(entry: any): string {
  if (!entry || typeof entry !== "object") return "seen";
  const statusCandidates = [
    entry.blockedFinalStatus,
    entry.kcStatusAfter,
    entry.kcStatus,
    entry.statusAfter,
    entry.status,
    entry.resultStatus,
    entry.statusLabel,
    entry.progressStatus,
    entry.result && entry.result.status
  ];
  for (const candidate of statusCandidates) {
    if (typeof candidate === "string" && candidate.trim().length) {
      return candidate;
    }
  }
  return "seen";
}

const exposureRows = computed(() => {
  if (!kc.value || !Array.isArray(kc.value.exposureHistory)) return [];
  return (kc.value.exposureHistory as ExposureHistory[])
    .slice()
    .reverse()
    .map((entry, idx) => {
      const date = formatTimestamp(entry.timestamp as any);
      return {
        rowKey: `${date}_${idx}`,
        isCorrect: Boolean(entry.isCorrect),
        displayedAt: date,
        rawEntry: entry
      };
    });
});

function buildDefaultExposureSummaryRow(row: any) {
  const entry = row.rawEntry || {};
  const statusLabel = getExposureStatusLabel(entry);
  return {
    rowKey: row.rowKey,
    date: row.displayedAt,
    question: getExposureQuestionLabel(entry, row),
    status: statusLabel,
    statusSource: getExposureStatusSource(entry),
    exposure: entry
  };
}

function buildBlockedAggregateSummaryRow(groupRows: any[], blockedSessionKey: string) {
  const safeRows = Array.isArray(groupRows) ? groupRows : [];
  const representativeRow = safeRows[0] || null;
  const representativeEntry = representativeRow?.rawEntry || {};
  const sessionEntries = safeRows.map((row: any) => row.rawEntry || {}).filter(Boolean);

  const parsedCompletionDates = sessionEntries
    .map((entry: any) => parseOptionalDate(entry.blockedCompletedAt))
    .filter(Boolean) as Date[];
  const completedAt = parsedCompletionDates.length
    ? new Date(Math.max(...parsedCompletionDates.map(date => date.getTime())))
    : parseOptionalDate(representativeEntry.timestamp);

  const totalQuestionsCandidate = sessionEntries
    .map((entry: any) => Number(entry.blockedTotalQuestions))
    .find((value: number) => Number.isFinite(value) && value > 0);
  const totalQuestions = totalQuestionsCandidate || safeRows.length || 10;

  const aggregateLabelCandidate = sessionEntries
    .map((entry: any) => (typeof entry.blockedAggregateLabel === "string" ? entry.blockedAggregateLabel.trim() : ""))
    .find(Boolean);
  const aggregateLabel = aggregateLabelCandidate || `Blocked Practice (x${totalQuestions})`;

  const finalStatusCandidate = sessionEntries
    .map((entry: any) =>
      typeof entry.blockedFinalStatus === "string" && entry.blockedFinalStatus.trim()
        ? entry.blockedFinalStatus.trim()
        : null
    )
    .find(Boolean);

  const aggregateEntry = {
    ...representativeEntry,
    blockedSessionKey,
    blockedTotalQuestions: totalQuestions,
    blockedAggregateLabel: aggregateLabel,
    blockedCompletedAt: completedAt || representativeEntry.blockedCompletedAt || representativeEntry.timestamp || null,
    blockedFinalStatus: finalStatusCandidate || representativeEntry.blockedFinalStatus || representativeEntry.status || null
  };

  return {
    rowKey: `blocked_${blockedSessionKey}`,
    date: completedAt ? formatTimestamp(completedAt) : representativeRow?.displayedAt || "—",
    question: getExposureQuestionLabel(aggregateEntry, representativeRow || {}),
    status: getExposureStatusLabel(aggregateEntry),
    statusSource: getExposureStatusSource(aggregateEntry),
    exposure: sessionEntries[0] || null,
    blockedSessionEntries: sessionEntries,
    aggregateEntry
  };
}

function groupCompletedBlockedRows(rows: any[]) {
  return (Array.isArray(rows) ? rows : []).reduce((groups: Record<string, any[]>, row: any) => {
    const entry = row?.rawEntry || {};
    if (!isBlockedAggregateCompleteEntry(entry)) {
      return groups;
    }
    const sessionKey = getBlockedAggregateSessionKey(entry, row);
    if (!sessionKey) {
      return groups;
    }
    if (!groups[sessionKey]) {
      groups[sessionKey] = [];
    }
    groups[sessionKey].push(row);
    return groups;
  }, {});
}

const exposureSummaryRows = computed(() => {
  const rows = exposureRows.value;
  const blockedGroupsBySession = groupCompletedBlockedRows(rows);

  const emittedBlockedSessions = new Set<string>();

  return rows.reduce((summaryRows: any[], row: any) => {
    const entry = row.rawEntry || {};
    const blockedSessionKey = getBlockedAggregateSessionKey(entry, row);
    const blockedGroup = blockedSessionKey ? blockedGroupsBySession[blockedSessionKey] : null;

    if (blockedSessionKey && blockedGroup && blockedGroup.length) {
      if (emittedBlockedSessions.has(blockedSessionKey)) {
        return summaryRows;
      }
      emittedBlockedSessions.add(blockedSessionKey);
      summaryRows.push(buildBlockedAggregateSummaryRow(blockedGroup, blockedSessionKey));
      return summaryRows;
    }

    summaryRows.push(buildDefaultExposureSummaryRow(row));
    return summaryRows;
  }, []);
});

// table column definition for DataTable component
const tableColumns = computed(() => [
  { key: "date", label: "Date" },
  { key: "question", label: "Question(s)" },
  { key: "status", label: "Status" }
]);

const etymologyText = computed(() => {
  if (!kc.value || !kc?.value?.word) return "";
  const wordName = String(kc?.value?.word ?? "").trim();
  const wordData = wordName ? lingoStore.getWordByName(wordName) : null;
  const spellingDesc = wordData && (wordData.spellingDescription);
  if (!spellingDesc || typeof spellingDesc !== "string") return "";
  return spellingDesc.replace(/\*\*\*(.+?)\*\*\*/g, "<strong>$1</strong>");
});

const fullExplanationFallbackText = computed(() => {
  if (!kc.value || !kc?.value?.word) return "";
  const wordName = String(kc?.value?.word ?? "").trim();
  const wordData = wordName ? lingoStore.getWordByName(wordName) : null;
  const spellingDescription = wordData && wordData.spellingDescription;
  return typeof spellingDescription === "string" ? spellingDescription : "";
});

const selectedFullExplanationPersona = computed(() => {
  const name = studentLingoData.value?.selectedTeacherBot?.name;
  if (typeof name === "string" && name.trim().length) {
    return name.trim();
  }
  return "Dot";
});

// Modal state
const showExposureModal = ref(false);
const selectedExposureRow = ref<any | null>(null);
const selectedExposure = ref<ExposureHistory | null>(null);
const selectedBlockedSessionEntries = ref<ExposureHistory[]>([]);
const showFullExplanationModal = ref(false);
const fullExplanationLoading = ref(false);
const fullExplanationContent = ref<{ spelling: string; vocabulary: string } | null>(null);

const fullExplanationByThread = computed(() => {
  if (!fullExplanationContent.value) {
    return "";
  }
  return fullExplanationContent.value.spelling || "";
});
// derived helpers for exposure rendering
const isClozeQuestion = computed(() => {
  return !!(selectedExposure.value && String(selectedExposure.value.itemCategory) === "cloze");
});
const isSpotQuestion = computed(() => {
  return !!(selectedExposure.value && String(selectedExposure.value.itemCategory) === "spot_error");
});
const isAudio = computed(() => {
  return !!(selectedExposure.value && String(selectedExposure.value.itemCategory) === "audio");
});

function getQuestionDataFromExposure(): any | null {
  if (!selectedExposure.value) return null;
  const q = selectedExposure.value.question ?? null;
  // common shapes: question.questionData or questionData at top-level
  const qd = q?.questionData ?? (q && typeof q === "object" ? q : null);
  return qd;
}

const questionData = computed(() => getQuestionDataFromExposure());

const sentenceWithOptions = computed(() => {
  const qd = questionData.value;
  if (!qd || !qd.sentence) return "";
  const rawOptions = Array.isArray(qd.options)
    ? qd.options
    : Array.isArray(qd.choices)
      ? qd.choices
      : [];
  const options = rawOptions.map((o: any) => String(o)).join(", ");
  const sentence = String(qd.sentence);
  if (options) {
    const word = kc.value?.word ? String(kc.value.word) : "";
    if (word) {
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const wordRegex = new RegExp(`\\b(${escapedWord})\\b`, "gi");
      if (wordRegex.test(sentence)) {
        return sentence.replace(wordRegex, `[${options}]`);
      }
    }
    if (sentence.includes("___")) {
      return sentence.replace("___", `[${options}]`);
    }
    return `${sentence} [${options}]`;
  }
  return sentence;
});

const spotQuestionSentence = computed(() => {
  if (!isSpotQuestion.value) return "";
  const qd = questionData.value;
  const sentenceSource = qd?.sentence ?? selectedExposure.value?.question?.generatedSentence ?? "";
  const sentence = String(sentenceSource);
  const word = kc.value?.word ? String(kc.value.word) : "";
  const wrongChoice =
    selectedExposure.value?.question?.wrongChoiceUsed ??
    selectedExposure.value?.question?.questionData?.wrongChoiceUsed ??
    "";
  if (!word || !wrongChoice) return sentence;
  const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const wordRegex = new RegExp(`\\b(${escapedWord})\\b`, "gi");
  if (wordRegex.test(sentence)) {
    return sentence.replace(wordRegex, String(wrongChoice));
  }
  return sentence;
});

const sentenceWithGivenAnswer = computed(() => {
  const qd = questionData.value;
  if (!qd || !selectedExposure.value) return "";
  qd.sentence = selectedExposure.value.question.generatedSentence;
  const given = selectedExposure.value.givenAnswer ?? "";
  if (selectedExposure.value.itemCategory === "audio") {
    return String(given);
  } else if (selectedExposure.value.itemCategory === "spot_error") {
    return escapeHtml(`${String(given)}`);
  } else if (selectedExposure.value.itemCategory === "cloze") {
    const sentenceSource = qd.sentence ?? selectedExposure.value.question?.generatedSentence ?? "";
    const sentence = String(sentenceSource);
    const answer = String(given);
    const boldAnswer = `<strong>${escapeHtml(answer)}</strong>`;
    const word = kc.value?.word ? String(kc.value.word) : "";
    if (word) {
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const wordRegex = new RegExp(`\\b(${escapedWord})\\b`, "gi");
      if (wordRegex.test(sentence)) {
        return sentence.replace(wordRegex, boldAnswer);
      }
    }
    if (sentence.includes("___")) {
      return sentence.replace("___", boldAnswer);
    }
    return boldAnswer;
  }
  //fallback
  return String(qd.sentence).replace("___", "<strong>" + escapeHtml(`${String(given)}`) + "</strong>");
});

const correctedSentenceWithGivenAnswer = computed(() => {
  const qd = questionData.value;
  if (!qd || !qd.sentence || !selectedExposure.value) return "";
  const correct = selectedExposure.value.question?.questionData?.choices?.correctChoice ?? selectedExposure.value.word;
  if (selectedExposure.value.itemCategory === "audio") {
    return String(correct);
  } else if (selectedExposure.value.itemCategory === "spot_error") {
    return escapeHtml(`${String(correct)}`);
  } else if (selectedExposure.value.itemCategory === "cloze") {
    const sentenceSource = qd.sentence ?? selectedExposure.value.question?.generatedSentence ?? "";
    const sentence = String(sentenceSource);
    const answer = String(correct ?? "");
    const boldAnswer = `<strong>${escapeHtml(answer)}</strong>`;
    const word = kc.value?.word ? String(kc.value.word) : "";
    if (word) {
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const wordRegex = new RegExp(`\\b(${escapedWord})\\b`, "gi");
      if (wordRegex.test(sentence)) {
        return sentence.replace(wordRegex, boldAnswer);
      }
    }
    if (sentence.includes("___")) {
      return sentence.replace("___", boldAnswer);
    }
    return boldAnswer;
  }
  return String(qd.sentence).replace("___", "<strong>" + escapeHtml(`${String(correct)}`) + "</strong>");
});

const resultClass = computed(() => {
  if (!selectedExposure.value) return "";
  return selectedExposure.value.isCorrect ? "result-correct" : "result-incorrect";
});

const questionTranscript = computed(() => {
  const qd = questionData.value;
  return qd?.transcript ?? selectedExposure.value?.question?.transcript ?? null;
});

const audioPromptLabel = computed(() => {
  const prompt = String(selectedExposure.value?.question?.audioPrompt ?? "Audio question.");
  const word = kc.value?.word ? String(kc.value.word) : "";
  if (!word) return `Audio prompt: <i>${escapeHtml(prompt)}</i>`;
  const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Bold, italic, and add quotes to the assessed word
  let processed = prompt;
  // Replace first and second occurrence with different formatting
  let wordCount = 0;
  processed = processed.replace(
    new RegExp(`\\b(${escapedWord})\\b`, "gi"),
    (match) => {
      wordCount++;
      if (wordCount === 1) return `<strong>"${match}"</strong>`;
      if (wordCount === 2) return `<strong>${match}</strong>`;
      return match;
    }
  );
  // Add quotes around the last sentence after 'As in.' or 'as in.'
  processed = processed.replace(
    /(as in\.?)(\s*)(["']?)([^"'].*?)([.!?])\s*$/i,
    (match, p1, p2, p3, p4, p5) => {
      // If already quoted, don't double quote
      const trimmed = p4.trim();
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) return match;
      return `${p1}${p2}"${trimmed}${p5}"`;
    }
  );

  // Italicize everything after 'Audio prompt:'
  return `Audio prompt: <i>${processed}</i>`;
});

const isCorrect = computed(() => Boolean(selectedExposure.value?.isCorrect));

const questionTypeLabel = computed(() => {
  const category = String(selectedExposure.value?.itemCategory ?? "");
  if (isAudio.value) return "Audio";
  if (category === "spot_error") return "Spot";
  return "Question";
});

const isBlockedAggregateSelection = computed(() => selectedBlockedSessionEntries.value.length > 0);

const blockedAttemptRows = computed(() => {
  if (!selectedBlockedSessionEntries.value.length) return [];
  return [...selectedBlockedSessionEntries.value]
    .sort((left: any, right: any) => {
      const leftTs = parseOptionalDate(left?.timestamp);
      const rightTs = parseOptionalDate(right?.timestamp);
      if (!leftTs && !rightTs) return 0;
      if (!leftTs) return -1;
      if (!rightTs) return 1;
      return leftTs.getTime() - rightTs.getTime();
    })
    .map((entry: any, index: number) => {
      const questionData = entry?.question?.questionData || {};
      const generatedSentence = questionData.sentence || entry?.question?.generatedSentence || "";
      const prompt = entry?.question?.audioPrompt || entry?.question?.transcript || "Audio question.";
      const questionText =
        entry?.itemCategory === "audio"
          ? String(prompt)
          : generatedSentence
            ? String(generatedSentence)
            : "Review the question.";

      const correctAnswer =
        entry?.correctAnswer ||
        entry?.question?.questionData?.choices?.correctChoice ||
        entry?.word ||
        "";

      return {
        attemptNumber: index + 1,
        isCorrect: Boolean(entry?.isCorrect),
        timestamp: formatTimestamp(entry?.timestamp || selectedExposureRow.value?.date),
        questionText,
        studentAnswer: entry?.givenAnswer ? String(entry.givenAnswer) : "",
        correctAnswer: correctAnswer ? String(correctAnswer) : ""
      };
    });
});

const blockedAttemptTotalQuestions = computed(() => {
  const aggregateEntry = selectedExposureRow.value?.aggregateEntry || {};
  const configured = Number(aggregateEntry.blockedTotalQuestions);
  if (Number.isFinite(configured) && configured > 0) {
    return configured;
  }
  return blockedAttemptRows.value.length || 10;
});

function buildSpellingAnswerReviewPayload(exposureEntry: any, options: { timestampOverride?: string | null } = {}) {
  if (!exposureEntry) return null;

  const entry = exposureEntry;
  const question = entry.question || {};

  const itemCategory = entry.itemCategory || question.itemCategory || "audio";
  const fallbackWord = entry.word || question.word || (question.questionData && question.questionData.word) || (kc.value?.word ?? "") || "";

  const optionData = extractOptionsFromQuestion(question, entry, itemCategory);
  const optionTexts = optionData.texts;
  const optionEntries = optionData.entries.length
    ? optionData.entries
    : optionTexts.map((text: string, index: number) => ({
        id: index,
        text,
        meta: optionData.meta[index] || null
      }));

  const selectedIndex = resolveSelectedIndex(entry, optionTexts);
  const correctIndex = resolveCorrectIndex(entry, optionTexts, fallbackWord, optionData.meta, optionData.additionalCorrectAnswers);

  const questionContent = buildQuestionContent(itemCategory, question.questionData || question || {}, fallbackWord);
  const studentAnswer = resolveStudentAnswer(entry, optionTexts, itemCategory, selectedIndex);
  const correctAnswer = resolveCorrectAnswer(entry, optionTexts, itemCategory, correctIndex, fallbackWord, optionData.meta, optionData.additionalCorrectAnswers);

  let studentSentenceText: string | null = null;
  let studentSentenceHtml: string | null = null;
  let correctSentenceText: string | null = null;
  let correctSentenceHtml: string | null = null;

  if (itemCategory === "cloze" && questionContent.clozeContext) {
    const { prefix, suffix, casedWord } = questionContent.clozeContext;
    const studentSentence = composeClozeSentence(prefix, suffix, studentAnswer, casedWord);
    const correctSentence = composeClozeSentence(prefix, suffix, correctAnswer, casedWord);
    studentSentenceText = studentSentence.text;
    studentSentenceHtml = studentSentence.html;
    correctSentenceText = correctSentence.text;
    correctSentenceHtml = correctSentence.html;
  }

  return {
    statusTitle: entry.isCorrect ? "Correct Answer" : "Incorrect Answer",
    word: fallbackWord,
    timestamp: options.timestampOverride || formatTimestamp(entry.timestamp),
    itemCategory,
    type: "spelling",
    isCorrect: Boolean(entry.isCorrect),
    questionText: questionContent.text,
    questionHtml: questionContent.html,
    questionDetails: questionContent.details,
    options: optionEntries,
    selectedIndex,
    correctIndex,
    studentAnswer,
    correctAnswer,
    studentSentence: studentSentenceHtml,
    studentSentenceHtml,
    studentSentenceText,
    studentAnswerSentence: studentSentenceText,
    studentAnswerSentenceHtml: studentSentenceHtml,
    correctSentence: correctSentenceHtml,
    correctSentenceHtml,
    correctSentenceText,
    correctAnswerSentence: correctSentenceText,
    correctAnswerSentenceHtml: correctSentenceHtml,
    fullExplanationFallback: fullExplanationFallbackText.value
  };
}

const activeAnswerReview = computed(() => {
  if (selectedBlockedSessionEntries.value.length) {
    const summaryRow = selectedExposureRow.value || {};
    const aggregateEntry = summaryRow.aggregateEntry || {};
    const orderedEntries = sortBlockedSessionEntries(selectedBlockedSessionEntries.value);

    const attempts = orderedEntries
      .map((entry: any, index: number) => {
        const entryTimestamp = formatTimestamp(entry?.timestamp || aggregateEntry.blockedCompletedAt || summaryRow?.date);
        const payload = buildSpellingAnswerReviewPayload(entry, { timestampOverride: entryTimestamp });
        if (!payload) return null;
        return {
          ...payload,
          attemptNumber: index + 1,
          isCorrect: Boolean(entry?.isCorrect)
        };
      })
      .filter(Boolean);

    const totalQuestionsRaw = Number(aggregateEntry.blockedTotalQuestions);
    const totalQuestions =
      Number.isFinite(totalQuestionsRaw) && totalQuestionsRaw > 0
        ? totalQuestionsRaw
        : attempts.length || 10;

    return {
      word: aggregateEntry.word || selectedExposure.value?.word || kc.value?.word || "",
      timestamp: summaryRow?.date || formatTimestamp(aggregateEntry.blockedCompletedAt || aggregateEntry.timestamp),
      itemCategory: "blocked_practice",
      type: "spelling",
      isCorrect: false,
      questionText: "",
      questionHtml: "",
      questionDetails: [],
      isBlockedAggregate: true,
      blockedAttempts: attempts,
      blockedTotalQuestions: totalQuestions,
      blockedAggregateLabel: aggregateEntry.blockedAggregateLabel || `Blocked Practice (x${totalQuestions})`,
      blockedFinalStatus: aggregateEntry.blockedFinalStatus || aggregateEntry.status || null,
      fullExplanationFallback: fullExplanationFallbackText.value
    };
  }

  return buildSpellingAnswerReviewPayload(selectedExposure.value);
});

function collectBlockedSessionEntriesFromSummaryRow(summaryRow: any): ExposureHistory[] {
  const directEntries = Array.isArray(summaryRow?.blockedSessionEntries)
    ? summaryRow.blockedSessionEntries.filter((entry: any) => entry && typeof entry === "object")
    : [];
  const sortedDirectEntries = sortBlockedSessionEntries(directEntries as ExposureHistory[]);

  const aggregateEntry =
    summaryRow?.aggregateEntry ||
    summaryRow?.rawEntry ||
    summaryRow?.exposure ||
    summaryRow?.original?.rawEntry ||
    null;
  if (!aggregateEntry || typeof aggregateEntry !== "object") {
    return sortedDirectEntries;
  }

  const rowLabel = typeof summaryRow?.question === "string" ? summaryRow.question.trim().toLowerCase() : "";
  const rowLooksBlocked = rowLabel.includes("blocked practice") || isBlockedTaggedEntry(aggregateEntry);

  const allEntries = exposureRows.value
    .map((row: any) => row?.rawEntry || null)
    .filter((entry: any) => entry && typeof entry === "object");
  if (!allEntries.length) {
    return sortedDirectEntries;
  }

  const directTotalQuestions = Number(
    summaryRow?.aggregateEntry?.blockedTotalQuestions ||
    summaryRow?.blockedTotalQuestions ||
    sortedDirectEntries[0]?.blockedTotalQuestions
  );
  const expectedTotalQuestions = Number.isFinite(directTotalQuestions) && directTotalQuestions > 0
    ? directTotalQuestions
    : 10;

  if (sortedDirectEntries.length >= expectedTotalQuestions) {
    return sortedDirectEntries;
  }

  const sessionKey = getBlockedAggregateSessionKey(aggregateEntry, summaryRow || {});
  if (sessionKey) {
    const keyedEntries = sortBlockedSessionEntries(allEntries.filter((entry: any) => {
      const entrySessionKey = getBlockedAggregateSessionKey(entry, summaryRow || {});
      return entrySessionKey === sessionKey;
    }) as ExposureHistory[]);
    if (keyedEntries.length) {
      return keyedEntries;
    }
  }

  const completionDate = parseOptionalDate(aggregateEntry.blockedCompletedAt);
  if (completionDate) {
    const completedAtMs = completionDate.getTime();
    const sameCompletionEntries = sortBlockedSessionEntries(allEntries.filter((entry: any) => {
      const entryCompletionDate = parseOptionalDate(entry?.blockedCompletedAt);
      return entryCompletionDate && entryCompletionDate.getTime() === completedAtMs;
    }) as ExposureHistory[]);
    if (sameCompletionEntries.length) {
      return sameCompletionEntries;
    }
  }

  const aggregateLabel =
    typeof aggregateEntry.blockedAggregateLabel === "string"
      ? aggregateEntry.blockedAggregateLabel.trim()
      : "";
  if (aggregateLabel) {
    const labelEntries = sortBlockedSessionEntries(allEntries.filter((entry: any) => {
      return (
        typeof entry?.blockedAggregateLabel === "string" &&
        entry.blockedAggregateLabel.trim() === aggregateLabel
      );
    }) as ExposureHistory[]);
    if (labelEntries.length) {
      return labelEntries;
    }
  }

  if (!rowLooksBlocked) {
    return sortedDirectEntries;
  }

  const wordToken = normalizeToken(aggregateEntry.word || summaryRow?.word || kc.value?.word);
  const blockedModeEntries = allEntries
    .filter((entry: any) => isBlockedTaggedEntry(entry))
    .filter((entry: any) => !wordToken || normalizeToken(entry.word) === wordToken)
    .sort((left: any, right: any) => {
      const leftTs = parseOptionalDate(left?.timestamp);
      const rightTs = parseOptionalDate(right?.timestamp);
      if (!leftTs && !rightTs) return 0;
      if (!leftTs) return -1;
      if (!rightTs) return 1;
      return leftTs.getTime() - rightTs.getTime();
    }) as ExposureHistory[];

  if (!blockedModeEntries.length) {
    return sortedDirectEntries;
  }

  const configuredTotal = Number(aggregateEntry.blockedTotalQuestions);
  const fallbackTotal = Number.isFinite(configuredTotal) && configuredTotal > 0 ? configuredTotal : 10;
  const fallbackEntries = sortBlockedSessionEntries(blockedModeEntries.slice(-fallbackTotal));

  if (fallbackEntries.length >= sortedDirectEntries.length) {
    return fallbackEntries;
  }

  return sortedDirectEntries;
}

function escapeHtml(str: string) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeRegExp(value: string) {
  return String(value ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeOptionText(option: any): string {
  if (option == null) return "";
  if (typeof option === "string") return option.trim();
  if (typeof option === "object") {
    const candidates = ["text", "label", "value", "answer"];
    for (const key of candidates) {
      if (option[key]) return String(option[key]).trim();
    }
  }
  return String(option).trim();
}

function isSkipGivenAnswer(value: string): boolean {
  if (typeof value !== "string") return false;
  return value.trim().toLowerCase() === "i don't know";
}

function formatSkipGivenAnswerDisplay(value: string): string {
  if (!isSkipGivenAnswer(value)) return value;
  return '"I don\'t know"';
}

function highlightAnswerWord(text: string, word: string, highlightClass = "answer-review__sentence-highlight"): string {
  const safeText = escapeHtml(text || "");
  if (!word || typeof word !== "string" || !word.trim().length) return safeText;
  const escapedWord = escapeRegExp(word.trim());
  if (!escapedWord) return safeText;
  const regex = new RegExp(`(${escapedWord})`, "gi");
  return safeText.replace(regex, `<span class="${highlightClass}">$1</span>`);
}

function collectClozeOptions(questionData: any, fallbackWord: string): string[] {
  const options: string[] = [];
  const seen = new Set<string>();
  const addFromSource = (source: any) => {
    if (!Array.isArray(source)) return;
    source.forEach((option: any) => {
      const normalized = normalizeOptionText(option);
      const key = normalized.toLowerCase();
      if (normalized && !seen.has(key)) {
        options.push(normalized);
        seen.add(key);
      }
    });
  };
  addFromSource(questionData.options);
  if (questionData?.choices && Array.isArray(questionData.choices.choices)) {
    addFromSource(questionData.choices.choices);
  }
  if (questionData?.choices && Array.isArray(questionData.choices.options)) {
    addFromSource(questionData.choices.options);
  }
  if (!options.length && typeof fallbackWord === "string" && fallbackWord.trim().length) {
    options.push(fallbackWord.trim());
  }
  return options;
}

// Mirror a template word's casing onto a replacement (ALL-CAPS / Title-case / lowercase).
function applyReplacementCasing(template: string, replacement: string): string {
  if (!template) return replacement;
  if (template === template.toUpperCase()) return replacement.toUpperCase();
  if (template === template.toLowerCase()) return replacement.toLowerCase();
  if (template[0] === template[0].toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

// Detect the target word's real casing: its own casing if it carries a capital, else the
// casing of its first whole-word occurrence in the sentence, else the lowercase lemma.
function resolveClozeWordCasing(correctWord: unknown, sentence?: unknown): string {
  const word =
    typeof correctWord === "string" ? correctWord.trim() : correctWord == null ? "" : String(correctWord).trim();
  if (!word) return "";
  if (word !== word.toLowerCase()) return word;
  const text = typeof sentence === "string" ? sentence : "";
  if (text) {
    const match = new RegExp(`\\b${escapeRegExp(word)}\\b`, "i").exec(text);
    if (match && match[0]) return match[0];
  }
  return word;
}

// Display transform for one Cloze option/answer: mirror the target word's casing. Never
// invents capitalization the word does not have, so a lowercase answer stays lowercase.
function formatClozeOptionForDisplay(option: unknown, casedWord: unknown): string {
  const value = typeof option === "string" ? option : option == null ? "" : String(option);
  if (!value) return value;
  const template = typeof casedWord === "string" ? casedWord : casedWord == null ? "" : String(casedWord);
  return applyReplacementCasing(template, value);
}

// No space after the prefix when it is empty, already ends in whitespace, or ends with an
// opening quote that should hug the next word (e.g. whispered, "Now). Otherwise a space is
// added — keeps standard mid-sentence cloze rendering unchanged.
function clozePrefixNeedsSpace(prefix: string): boolean {
  if (!prefix || /\s$/.test(prefix)) return false;
  return !/["'“‘«„]$/.test(prefix);
}

function composeClozeSentence(prefix: string, suffix: string, answer: string, casedWord = ""): { text: string; html: string } {
  const safePrefix = typeof prefix === "string" ? prefix : "";
  const safeSuffix = typeof suffix === "string" ? suffix : "";
  const normalizedAnswer = typeof answer === "string" && answer.trim().length ? answer.trim() : "________";
  // Display-only casing: mirror the target word's real casing onto the answer.
  const displayAnswer = formatClozeOptionForDisplay(normalizedAnswer, casedWord);
  const needsSpaceAfterPrefix = clozePrefixNeedsSpace(safePrefix);
  const needsSpaceBeforeSuffix = safeSuffix && !/^\s/.test(safeSuffix);
  const computedPrefix = needsSpaceAfterPrefix ? `${safePrefix} ` : safePrefix;
  const computedSuffix = needsSpaceBeforeSuffix ? ` ${safeSuffix}` : safeSuffix;
  const text = `${computedPrefix}${displayAnswer}${computedSuffix}`.replace(/\s+/g, " ").trim();
  const html = `${escapeHtml(computedPrefix)}<span class="answer-review__sentence-highlight">${escapeHtml(displayAnswer)}</span>${escapeHtml(computedSuffix)}`
    .replace(/\s+/g, " ")
    .trim();
  return { text, html };
}

function extractOptionsFromQuestion(question: any, entry: any, itemCategory: string) {
  const source = question && typeof question === "object" ? question : {};
  const questionData = source.questionData && typeof source.questionData === "object" ? source.questionData : source;
  const nestedChoices = questionData.choices && typeof questionData.choices === "object" ? questionData.choices : {};
  const texts: string[] = [];
  const meta: any[] = [];
  const seenOptions = new Set<string>();
  const additionalCorrectAnswers: string[] = [];
  const seenCorrectAnswers = new Set<string>();

  const collectOptions = (candidate: any): any[] => {
    if (!candidate) return [];
    if (Array.isArray(candidate)) return candidate;
    if (typeof candidate === "object") {
      return Object.keys(candidate).sort().reduce((acc: any[], key) => acc.concat(collectOptions(candidate[key])), []);
    }
    return [candidate];
  };

  const candidateSources = [
    questionData.options, nestedChoices.options, nestedChoices.choices, questionData.choices,
    questionData.answers, questionData.alternatives, entry.options, entry.choices
  ];

  for (const candidate of candidateSources) {
    const collection = collectOptions(candidate);
    if (Array.isArray(collection) && collection.length) {
      collection.forEach((option: any) => {
        const normalized = normalizeOptionText(option);
        const normalizedKey = normalized.toLowerCase();
        if (normalized && !seenOptions.has(normalizedKey)) {
          texts.push(normalized);
          meta.push(option);
          seenOptions.add(normalizedKey);
        }
      });
      if (texts.length) break;
    }
  }

  if (questionData && typeof questionData === "object") {
    const keys = Object.keys(questionData).filter(key => /option/i.test(key)).sort();
    if (keys.length) {
      keys.forEach(key => {
        const option = questionData[key];
        const normalized = normalizeOptionText(option);
        const normalizedKey = normalized.toLowerCase();
        if (normalized && !seenOptions.has(normalizedKey)) {
          texts.push(normalized);
          meta.push(option);
          seenOptions.add(normalizedKey);
        }
      });
    }
  }

  const questionCorrectAnswers = [
    questionData.correctAnswer, questionData.correctOption, questionData.expectedAnswer, questionData.answer,
    nestedChoices.correctChoice, nestedChoices.correctAnswer, nestedChoices.correct
  ];
  questionCorrectAnswers.forEach((answer: any) => {
    if (typeof answer === "string" && answer.trim().length) {
      const normalized = answer.trim();
      if (!seenCorrectAnswers.has(normalized.toLowerCase())) {
        additionalCorrectAnswers.push(normalized);
        seenCorrectAnswers.add(normalized.toLowerCase());
      }
    }
  });

  if (itemCategory === "cloze" && typeof source.word === "string" && source.word.trim().length) {
    const normalizedWord = source.word.trim();
    if (!seenCorrectAnswers.has(normalizedWord.toLowerCase())) {
      additionalCorrectAnswers.push(normalizedWord);
      seenCorrectAnswers.add(normalizedWord.toLowerCase());
    }
  }

  return {
    texts,
    meta,
    entries: texts.map((text, index) => ({ id: index, text, meta: meta[index] })),
    additionalCorrectAnswers
  };
}

function buildQuestionContent(itemCategory: string, questionData: any, fallbackWord: string) {
  const ensureText = (value: any): string => {
    if (typeof value !== "string") return "";
    return value.trim();
  };
  const normalizedFallback = ensureText(fallbackWord);
  const defaultText = normalizedFallback ? `Spell the word: ${normalizedFallback}` : "Review the question.";
  const details: any[] = [];
  let clozeContext: { prefix: string; suffix: string; casedWord: string } | null = null;
  let text = "";
  let html = "";

  if (itemCategory === "cloze") {
    const segments = Array.isArray(questionData.clozeSentenceSegments)
      ? questionData.clozeSentenceSegments.filter((segment: any) => segment != null)
      : [];
    const clozeOptions = collectClozeOptions(questionData, fallbackWord);
    // Detect the target word's real casing (its own casing, else from the sentence) so we can
    // mirror it onto every option — matching what the student saw in the activity.
    const casedWord = resolveClozeWordCasing(
      ensureText(questionData.word) || fallbackWord,
      ensureText(questionData.clozePlaybackSentence) ||
        ensureText(questionData.sentence) ||
        ensureText(questionData.generatedSentence)
    );
    if (segments.length) {
      const sanitized = segments.map((segment: any) => ensureText(segment));
      const prefix = sanitized[0] || "";
      const suffix = sanitized.slice(1).filter(Boolean).join(" ");
      clozeContext = { prefix, suffix, casedWord };
      const optionsText = clozeOptions.length
        ? clozeOptions.map((option: string) => formatClozeOptionForDisplay(option, casedWord)).join(", ")
        : "________";
      const bracketText = `[${optionsText}]`;
      // Opening quotes/brackets hug the next token (e.g. whispered, "[…]); every other prefix
      // keeps its separating space. Mirrors composeClozeSentence's spacing rules.
      const prefixSep = clozePrefixNeedsSpace(prefix) ? " " : "";
      const suffixSep = suffix && !/^\s/.test(suffix) ? " " : "";
      text = `${prefix}${prefixSep}${bracketText}${suffixSep}${suffix}`.replace(/\s+/g, " ").trim();
      const escapedPrefix = escapeHtml(prefix);
      const escapedSuffix = escapeHtml(suffix);
      const escapedOptions = escapeHtml(optionsText);
      const blankHtml = `<span class="answer-review__blank">[${escapedOptions}]</span>`;
      html = `${escapedPrefix}${prefixSep}${blankHtml}${suffixSep}${escapedSuffix}`.replace(/\s+/g, " ").trim();
    } else {
      text = ensureText(questionData.sentence) || ensureText(questionData.generatedSentence);
    }
    if (ensureText(questionData.clozePlaybackSentence)) {
      details.push({ label: "Sentence playback", value: ensureText(questionData.clozePlaybackSentence) });
    }
  } else if (itemCategory === "spot_error") {
    const kcLevel = kc.value?.level;
    const useBoldedIntro = kcLevel != null && Number(kcLevel) <= 4;
    const intro = useBoldedIntro
      ? "This sentence has one <span class='bolded'>bolded</span> word that is incorrect."
      : "This sentence has one word that is incorrect.";
    const outro = "Write the correct spelling of the word.";
    const wrongChoiceUsed = ensureText(questionData.wrongChoiceUsed);
    const correctWord = ensureText(questionData.word) || ensureText(fallbackWord);
    let sentence =
      ensureText(questionData.presentedSentence) ||
      ensureText(questionData.sentence) ||
      ensureText(questionData.generatedSentence);
    if (sentence && wrongChoiceUsed && correctWord && wrongChoiceUsed.toLowerCase() !== correctWord.toLowerCase()) {
      const correctWordRegex = new RegExp(`\\b${escapeRegExp(correctWord)}\\b`, "i");
      sentence = sentence.replace(correctWordRegex, wrongChoiceUsed);
    }
    const targetWord = wrongChoiceUsed || correctWord;
    let sentenceHtml = escapeHtml(sentence);
    if (sentence && targetWord && useBoldedIntro) {
      const sanitizedSentence = escapeHtml(sentence);
      const sanitizedTarget = escapeHtml(targetWord);
      const escapedTarget = escapeRegExp(sanitizedTarget);
      const regex = new RegExp(`(${escapedTarget})`, "i");
      sentenceHtml = sanitizedSentence.replace(regex, '<span class="bolded">$1</span>');
    }
    const introHtml = `<span class="answer-review__spot-error-intro">${intro}</span>`;
    const outroHtml = `<span class="answer-review__spot-error-outro">${escapeHtml(outro)}</span>`;
    const sentenceBlock = `<span class="answer-review__spot-error-sentence">${sentenceHtml || ""}</span>`;
    html = `${introHtml}${sentenceBlock}${outroHtml}`;
    text = [intro, sentence, outro].filter(Boolean).join(" ");
  } else if (itemCategory === "audio") {
    const audioPrompt = ensureText(questionData.audioPrompt);
    if (audioPrompt) {
      const highlightedPrompt = highlightAnswerWord(audioPrompt, fallbackWord, "answer-review__audio-highlight");
      text = `Audio prompt: ${audioPrompt}`;
      html = `<span class="answer-review__audio-label">Audio prompt:</span> <i><span class="answer-review__audio-text">${highlightedPrompt}</span></i>`;
    } else {
      text =
        ensureText(questionData.sentence) ||
        ensureText(questionData.generatedSentence) ||
        `Spell the word: ${normalizedFallback}`;
    }
  } else {
    text =
      ensureText(questionData.sentence) ||
      ensureText(questionData.generatedSentence) ||
      ensureText(questionData.prompt) ||
      ensureText(questionData.text);
  }

  if (!text && !html) {
    text = defaultText;
  }

  return { text, html, details, clozeContext };
}

function resolveSelectedIndex(entry: any, optionTexts: string[]): number | null {
  const size = optionTexts.length;
  if (!size) return null;
  const questionData = entry?.question?.questionData || {};
  const candidates = [
    entry.selectedIndex, entry.selectedOptionIndex, entry.answerIndex, entry.answerChoiceIndex,
    questionData.selectedIndex, questionData.selectedOptionIndex, questionData.answerIndex, questionData.answerChoiceIndex
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && candidate >= 0 && candidate < size) return candidate;
  }
  const givenAnswer =
    entry.givenAnswer || entry.answer || entry.selectedOption || entry.response ||
    questionData.playerAnswer || questionData.submittedAnswer;
  if (typeof givenAnswer === "string" && size) {
    const foundIndex = optionTexts.findIndex(option => option.toLowerCase() === givenAnswer.toLowerCase());
    if (foundIndex !== -1) return foundIndex;
  }
  return null;
}

function resolveCorrectIndex(
  entry: any, optionTexts: string[], fallbackWord: string, rawOptions: any[] = [], additionalCorrectAnswers: string[] = []
): number | null {
  const size = optionTexts.length;
  if (!size) return null;
  const questionData = entry?.question?.questionData || {};
  const flaggedIndex = rawOptions.findIndex(option => option && option.isCorrect === true);
  if (flaggedIndex !== -1) return flaggedIndex;
  const candidates = [
    entry.correctIndex, entry.correctOptionIndex, entry.correctAnswerIndex, entry.correctChoiceIndex,
    questionData.correctIndex, questionData.correctOptionIndex, questionData.correctAnswerIndex, questionData.correctChoiceIndex
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && candidate >= 0 && candidate < size) return candidate;
  }
  const correctAnswer =
    entry.correctAnswer || entry.answerKey || entry.expectedAnswer || entry.correctOption || entry.correctResponse;
  if (typeof correctAnswer === "string" && size) {
    const foundIndex = optionTexts.findIndex(option => option.toLowerCase() === correctAnswer.toLowerCase());
    if (foundIndex !== -1) return foundIndex;
  }
  if (Array.isArray(additionalCorrectAnswers) && additionalCorrectAnswers.length) {
    for (const candidate of additionalCorrectAnswers) {
      if (typeof candidate === "string" && candidate.trim().length) {
        const normalized = candidate.trim().toLowerCase();
        const matchIndex = optionTexts.findIndex(option => option.toLowerCase() === normalized);
        if (matchIndex !== -1) return matchIndex;
      }
    }
  }
  if (typeof fallbackWord === "string" && fallbackWord.trim().length) {
    const normalizedFallback = fallbackWord.trim().toLowerCase();
    const fallbackIndex = optionTexts.findIndex(option => option.toLowerCase() === normalizedFallback);
    if (fallbackIndex !== -1) return fallbackIndex;
  }
  return null;
}

function resolveStudentAnswer(entry: any, optionTexts: string[], itemCategory: string, selectedIndex: number | null): string {
  const questionData = entry?.question?.questionData || {};
  const answerCandidates = [
    entry.givenAnswer, entry.answer, entry.selectedOption, entry.response,
    questionData.playerAnswer, questionData.submittedAnswer, questionData.answer
  ];
  for (const candidate of answerCandidates) {
    if (candidate != null) {
      const asString = String(candidate).trim();
      if (asString.length) return formatSkipGivenAnswerDisplay(asString);
    }
  }
  if (itemCategory === "cloze" && selectedIndex != null && selectedIndex >= 0 && selectedIndex < optionTexts.length) {
    return optionTexts[selectedIndex];
  }
  return "";
}

function resolveCorrectAnswer(
  entry: any, optionTexts: string[], itemCategory: string, correctIndex: number | null,
  fallbackWord: string, rawOptions: any[] = [], additionalCorrectAnswers: string[] = []
): string {
  const flaggedOption = rawOptions.find(option => option && option.isCorrect === true);
  if (flaggedOption) {
    const normalized = normalizeOptionText(flaggedOption);
    if (normalized) return normalized;
  }
  const questionData = entry?.question?.questionData || {};
  const answerCandidates = [
    entry.correctAnswer, entry.answerKey, entry.expectedAnswer, entry.correctOption, entry.correctResponse,
    entry.question?.correctAnswer, questionData.correctAnswer, questionData.correctOption,
    questionData.expectedAnswer, questionData.answer, questionData.correctChoice
  ];
  for (const candidate of answerCandidates) {
    if (candidate != null) {
      const asString = String(candidate).trim();
      if (asString.length) return asString;
    }
  }
  if (Array.isArray(additionalCorrectAnswers) && additionalCorrectAnswers.length) {
    const candidate = additionalCorrectAnswers.find(value => typeof value === "string" && value.trim().length);
    if (candidate) return candidate.trim();
  }
  if (itemCategory === "cloze" && correctIndex != null && correctIndex >= 0 && correctIndex < optionTexts.length) {
    return optionTexts[correctIndex];
  }
  if (fallbackWord != null) {
    const asString = String(fallbackWord).trim();
    if (asString.length) return asString;
  }
  return "";
}

watch(showExposureModal, open => {
  if (!open) {
    selectedExposureRow.value = null;
    selectedExposure.value = null;
    selectedBlockedSessionEntries.value = [];
  }
});

// Fetch word by its actual KC word when KC is available
watch(
  kc,
  async (newKc) => {
    const word = newKc?.word;
    if (!word) return;
    isLoading.value = true;
    try {
      await lingoStore.fetchWordByName(decodeURIComponent(String(word)));
    } catch (err) {
      console.error("Failed to fetch word by name:", err);
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true }
);

const dataLoading = ref(false);
// Tracks whether the last data fetch succeeded, so the template can distinguish
// a transient fetch failure (offer retry) from a genuine missing word.
const dataFetchOk = ref(true);

async function ensureStudentData(id: string) {
  if (!id) return;
  let fetchOk = true;
  // Already hold this student's record (e.g. navigated in from the list) — use it.
  if (!lingoStore.getLingoDataByStudentId(id)) {
    dataLoading.value = true;
    try {
      fetchOk = await lingoStore.fetchStudentLingoData({ studentIds: [id] });
    } finally {
      dataLoading.value = false;
    }
  }
  dataFetchOk.value = fetchOk;
  // Wait for `kc` to recompute against the freshly-loaded data before deciding
  await nextTick();
  reportIfWordMissing(fetchOk);
}

// Retry affordance for the fetch-failed empty state.
function retryLoad() {
  dataFetchOk.value = true;
  ensureStudentData(studentId.value);
}

// Dedupe key for the last reported miss so we don't fire the same alert twice
// (e.g. when both onMounted and the studentId watcher resolve to the same word).
let lastReportedMiss: string | null = null;

// Fire a Slack alert (via the client logger) when a word the user navigated to
// genuinely cannot be resolved after the student's data has finished loading.
function reportIfWordMissing(fetchOk: boolean) {
  if (dataLoading.value) return;          // still loading — not a miss
  if (normalizedKcId.value == null) return; // no word requested
  if (kc.value) return;                    // resolved fine — nothing to report

  const dedupeKey = `${studentId.value}:${kcIdParam.value}`;
  if (lastReportedMiss === dedupeKey) return;
  lastReportedMiss = dedupeKey;

  // On a genuine miss `kc` is null by definition, so the word is unknowable —
  // the failing id maps to no KC in the student's data. wordId + studentId are
  // enough to trace it (and the id fallback chain may itself be the word).
  logger.error(
    WORD_NOT_FOUND_EVENT,
    buildWordNotFoundLog({
      studentId: studentId.value,
      wordId: kcIdParam.value,
      word: kc.value?.word ?? null,
      listType: "spelling",
      availableKcCount: searchableKcs.value.length,
      dataFetchOk: fetchOk,
    })
  );
}

onMounted(() => ensureStudentData(studentId.value));

// The router reuses this component across /lingo-spelling-word/:studentId/:kcId
// navigations, so onMounted won't re-run. Refetch when the student changes.
watch(studentId, id => ensureStudentData(id));

// kcId-only navigation (word A → B, same student) reuses this component without
// re-running onMounted or the studentId watcher, so the miss-check would never
// re-fire. Re-check after `kc` recomputes against the already-loaded data.
watch(normalizedKcId, async () => {
  await nextTick();
  reportIfWordMissing(dataFetchOk.value);
});

// Level modal state
const showLevelModal = ref(false);
const showLevelIcon = ref(true);
const levelIcon = ref<string | null>(null);
const modalTitle = ref("");
const modalBodyHtml = ref("");
const activeKcForModal = ref<any | null>(null);

function getLevelHtml(level: number | null) {
  switch (Number(level)) {
    case 1:
      return i18n.global.t("lingo_v2.spelling.level_1_description").toString();
    case 2:
      return i18n.global.t("lingo_v2.spelling.level_2_description").toString();
    case 3:
      return i18n.global.t("lingo_v2.spelling.level_3_description").toString();
    case 4:
      return i18n.global.t("lingo_v2.spelling.level_4_description").toString();
    case 5:
      return i18n.global.t("lingo_v2.spelling.level_5_description").toString();
    case 6:
      return i18n.global.t("lingo_v2.spelling.level_6_description").toString();
    case 7:
      return i18n.global.t("lingo_v2.spelling.level_7_description").toString();
    default:
      return i18n.global.t("lingo_v2.spelling.level_1_description").toString();
  }
}

function openLevelModalFor(kcItem: any) {
  activeKcForModal.value = kcItem || null;
  const lvl = kcItem && kcItem.level ? appendItemLevel(kcItem.level) : null;
  modalTitle.value = lvl ? `Spelling Level ${lvl}` : "Spelling Level";
  modalBodyHtml.value = getLevelHtml(lvl);
  showLevelIcon.value = true;
  showLevelModal.value = true;
}

function openExposureModal(summaryRow: any, index?: number) {
  selectedExposureRow.value = summaryRow || null;
  const blockedEntries = collectBlockedSessionEntriesFromSummaryRow(summaryRow);
  selectedBlockedSessionEntries.value = blockedEntries;
  if (blockedEntries.length) {
    selectedExposure.value = blockedEntries[0] || null;
  } else {
    selectedExposure.value = summaryRow?.exposure ?? null;
  }
  showExposureModal.value = Boolean(selectedExposureRow.value);
  nextTick(() => {
    console.log("Answers List (Dashboard Spelling):", activeAnswerReview.value);
  });
}

function closeExposureModal() {
  showExposureModal.value = false;
  selectedExposureRow.value = null;
  selectedExposure.value = null;
  selectedBlockedSessionEntries.value = [];
}

function closeAnswerReview() {
  closeExposureModal();
}

async function openFullExplanationFromReview() {
  const wordCandidate = activeAnswerReview.value?.word || kc.value?.word || "";
  const word = typeof wordCandidate === "string" ? wordCandidate.trim() : "";
  const persona = selectedFullExplanationPersona.value;

  showFullExplanationModal.value = true;

  if (!word || !persona) {
    fullExplanationContent.value = null;
    return;
  }

  fullExplanationLoading.value = true;
  try {
    const response = await api.post("/v2/lingo-v2/lingo-content/full-explanation", {
      word,
      persona
    });
    const payload = response?.data?.data;
    if (payload && typeof payload === "object") {
      fullExplanationContent.value = {
        spelling: typeof payload.spelling === "string" ? payload.spelling : "",
        vocabulary: typeof payload.vocabulary === "string" ? payload.vocabulary : ""
      };
    } else {
      fullExplanationContent.value = null;
    }
  } catch (error) {
    console.error("Failed to fetch full explanation for dashboard spelling review:", error);
    fullExplanationContent.value = null;
  } finally {
    fullExplanationLoading.value = false;
  }
}

function handleFullExplanationContinue() {
  showFullExplanationModal.value = false;
}

const selectedStudent = computed(() => learnerStore.getLearnerById(studentId.value) || null);

function capitalise(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const displayName = computed(() => {
  const student = selectedStudent.value;
  if (!student) return 'Unknown Student';
  return `${capitalise(student.firstName)} ${capitalise(student.lastName)}`.trim();
});

// Return translated tooltip text for an exposure row (falls back to KC status)
function getTooltipTextForExposure(row: any): string | null {
  const statusSource = row?.statusSource ?? row?.exposure?.status ?? kc.value?.status;
  if (!statusSource) return null;
  const tooltipKey = getKcStatusTooltip(String(statusSource));
  if (!tooltipKey) return null;
  return i18n.global.t(tooltipKey).toString();
}

function appendItemLevel(level) {
  const levelNum = Number(level);
  if (levelNum === 7) {
    return levelNum + "+";
  }
  return level;
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_page_title = _resolveComponent("page-title")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_router_link, {
            class: "back-btn",
            to: '/main/lingo-data/student/' + (studentId.value || ''),
            "aria-label": "Back to student lingo data"
          }, {
            default: _withCtx(() => [
              (!isArrowIconString)
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(ArrowLeftIcon)), {
                    key: 0,
                    class: "back-icon"
                  }))
                : (_openBlock(), _createElementBlock("img", {
                    key: 1,
                    src: _unref(ArrowLeftIcon),
                    alt: "Back",
                    class: "back-icon"
                  }, null, 8, _hoisted_5)),
              _hoisted_6
            ]),
            _: 1
          }, 8, ["to"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_component_page_title, {
            pages: pages.value,
            hideFilters: true,
            highlightLast: true
          }, null, 8, ["pages"])
        ]),
        _createElementVNode("h1", _hoisted_8, "Spell the word: " + _toDisplayString(kc.value?.word || "-"), 1),
        _createElementVNode("span", _hoisted_9, _toDisplayString(displayName.value), 1)
      ]),
      (dataLoading.value && !kc.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createVNode(SpinnerLoader),
            _hoisted_11
          ]))
        : (kc.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createElementVNode("article", {
                class: _normalizeClass(["summary-card", { 'summary-card--loading': isLoading.value }])
              }, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _createVNode(SpinnerLoader),
                      _hoisted_14
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("p", _hoisted_17, [
                      _createTextVNode(" Spell the word: "),
                      _createElementVNode("i", null, _toDisplayString(kc.value.word || "-"), 1)
                    ]),
                    _createElementVNode("div", _hoisted_18, [
                      _createElementVNode("span", {
                        class: "level-text open-modal",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (openLevelModalFor(kc.value))),
                        style: {"cursor":"pointer"}
                      }, " Level " + _toDisplayString(appendItemLevel(kc.value.level) ?? "-"), 1),
                      _hoisted_19,
                      _createElementVNode("span", _hoisted_20, _toDisplayString(statusBadge.value.label), 1),
                      _createVNode(LingoTooltipIcon, {
                        text: statusDescription.value,
                        placement: "top",
                        size: "sm",
                        ariaLabel: statusBadge.value.label
                      }, null, 8, ["text", "ariaLabel"])
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("p", {
                    class: "etymology-text",
                    innerHTML: etymologyText.value
                  }, null, 8, _hoisted_22)
                ]),
                _createElementVNode("div", _hoisted_23, [
                  _createVNode(LingoDataTable, {
                    columns: tableColumns.value,
                    items: exposureSummaryRows.value,
                    "row-key": 'rowKey',
                    "empty-text": 'No exposure recorded yet.',
                    tableClass: "table-borders"
                  }, {
                    "cell-question": _withCtx(({ item, index }) => [
                      _createElementVNode("button", {
                        class: "status-btn",
                        onClick: ($event: any) => (openExposureModal(item, index))
                      }, _toDisplayString(item.question), 9, _hoisted_24)
                    ]),
                    "cell-status": _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_25, [
                        _createElementVNode("span", _hoisted_26, _toDisplayString(item.status), 1),
                        (item.status !== '—' && getTooltipTextForExposure(item))
                          ? (_openBlock(), _createBlock(LingoTooltipIcon, {
                              key: 0,
                              text: getTooltipTextForExposure(item),
                              placement: "top",
                              size: "sm",
                              ariaLabel: item.status
                            }, null, 8, ["text", "ariaLabel"]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _: 1
                  }, 8, ["columns", "items", "empty-text"])
                ])
              ], 2)
            ]))
          : (!dataFetchOk.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                _hoisted_28,
                _hoisted_29,
                _createElementVNode("button", {
                  class: "close-modal-btn",
                  onClick: retryLoad
                }, "Retry")
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_30, _hoisted_33))
    ]),
    (selectedExposureRow.value)
      ? (_openBlock(), _createBlock(AnswerReviewModal, {
          key: 0,
          modelValue: showExposureModal.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showExposureModal).value = $event)),
          review: activeAnswerReview.value,
          "teacher-bot": studentLingoData.value?.selectedTeacherBot || null,
          onClose: closeAnswerReview,
          onOpenFullExplanation: openFullExplanationFromReview
        }, null, 8, ["modelValue", "review", "teacher-bot"]))
      : _createCommentVNode("", true),
    _createVNode(BlockedPracticeFullExplanationModal, {
      modelValue: showFullExplanationModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showFullExplanationModal).value = $event)),
      loading: fullExplanationLoading.value,
      content: fullExplanationByThread.value,
      "fallback-content": fullExplanationFallbackText.value,
      "teacher-bot": studentLingoData.value?.selectedTeacherBot || null,
      "allow-dismiss": true,
      onContinue: handleFullExplanationContinue
    }, null, 8, ["modelValue", "loading", "content", "fallback-content", "teacher-bot"]),
    _createVNode(LingoModal, {
      modelValue: showLevelModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showLevelModal).value = $event)),
      title: modalTitle.value,
      icon: showLevelIcon.value ? _unref(BuildingIcon) : '',
      "show-close-button": true,
      centered: ""
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", { innerHTML: modalBodyHtml.value }, null, 8, _hoisted_34)
      ]),
      footer: _withCtx(() => [
        _createElementVNode("button", {
          class: "close-modal-btn",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (showLevelModal.value = false))
        }, "Close")
      ]),
      _: 1
    }, 8, ["modelValue", "title", "icon"])
  ]))
}
}

})